import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, Eraser, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/Badge'
import type { SuggestionSource } from '@/services/api'

/** How long typing has to stop before the ERP is asked. */
const DEBOUNCE_MS = 300

/** Below this the query matches too much of the catalogue to be worth a round trip. */
const MINIMUM_QUERY_LENGTH = 2

/**
 * The list is wider than the field it hangs off.
 *
 * The field holds an article or customer number and is sized for one; the list holds that plus a
 * description, and at the field's width a name like "Axstores Distribution AB" is cut off at the
 * point where it stops being the thing you are choosing between.
 */
const MINIMUM_LIST_WIDTH = 340
const MAXIMUM_LIST_WIDTH = 560
/** Breathing room so the list never runs flush into the edge of the window. */
const VIEWPORT_MARGIN = 16
/** The list's tallest; it gets shorter when the window has less room than this. */
const MAXIMUM_LIST_HEIGHT = 288
/** Below this much room under the field, the list opens upward if there is more room above. */
const MINIMUM_ROOM_BELOW = 200
const LIST_GAP = 4

interface AnchorRect {
  /** One of top or bottom is set: the list hangs below the field, or stands above it. */
  top?: number
  bottom?: number
  left: number
  width: number
  /** Never taller than the room on its side, so every suggestion can be scrolled to. */
  maxHeight: number
}

/**
 * Follows the field the list hangs off, in viewport coordinates.
 *
 * The list cannot be a child of the field: both panels clip their contents to get rounded
 * corners, and a clipped child cannot be lifted out with z-index at any value. So it renders
 * into the body and is positioned here instead, which also means nothing on the page can stack
 * above it.
 */
function useAnchorRect(ref: React.RefObject<HTMLElement>, active: boolean): AnchorRect | null {
  const [rect, setRect] = useState<AnchorRect | null>(null)

  useLayoutEffect(() => {
    if (!active) {
      setRect(null)
      return
    }

    const measure = () => {
      const element = ref.current
      if (!element) return
      const bounds = element.getBoundingClientRect()
      const width = Math.min(
        Math.max(bounds.width, MINIMUM_LIST_WIDTH),
        Math.max(window.innerWidth - VIEWPORT_MARGIN * 2, MINIMUM_LIST_WIDTH),
        MAXIMUM_LIST_WIDTH,
      )
      // Keep the right edge on screen for a field near the window's edge.
      const left = Math.min(bounds.left, window.innerWidth - width - VIEWPORT_MARGIN)
      const roomBelow = window.innerHeight - bounds.bottom - LIST_GAP - VIEWPORT_MARGIN
      const roomAbove = bounds.top - LIST_GAP - VIEWPORT_MARGIN
      const upward = roomBelow < MINIMUM_ROOM_BELOW && roomAbove > roomBelow
      setRect({
        ...(upward
          ? { bottom: window.innerHeight - bounds.top + LIST_GAP }
          : { top: bounds.bottom + LIST_GAP }),
        left: Math.max(left, VIEWPORT_MARGIN),
        width,
        maxHeight: Math.max(Math.min(upward ? roomAbove : roomBelow, MAXIMUM_LIST_HEIGHT), 120),
      })
    }

    measure()
    // Scroll is captured because any ancestor scrolling moves the field, not just the window.
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [ref, active])

  return rect
}

/**
 * A suggestion, flattened to what the list needs.
 *
 * Articles and customers arrive under different field names but read the same way, so each
 * caller maps its own shape into this rather than the list knowing about either.
 */
export interface Suggestion {
  /** Stable key, and what the field is set to when this is picked. */
  value: string
  /** Shown after the value, separated by a colon. */
  description: string
  /**
   * Our own id for the thing suggested, where we hold one. The field is set from `value`; this
   * is for a caller that has to go and read the rest of what we know about it.
   */
  id?: string
}

/** Delays a value until it has stopped changing, so a query follows typing rather than keystrokes. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return settled
}

/**
 * A value of ours that is looked up in the ERP rather than typed from memory.
 *
 * It reads as text until clicked, the same as any other correctable value on this page. Opening
 * it turns it into a search: what is typed is a query, not the value, and the value only changes
 * when a suggestion is taken. Typing something the ERP does not know therefore cannot silently
 * become the answer.
 *
 * Nothing is saved. A taken suggestion lives in this component and goes no further.
 */
export function ErpTypeahead({
  value,
  onPick,
  placeholder,
  label,
  queryKey,
  search,
  enabled = true,
  renderResting,
  iconClassName = 'h-3 w-3',
  tone = 'default',
  readOnly = false,
}: {
  value: string
  onPick: (suggestion: Suggestion) => void
  placeholder: string
  label: string
  /** Prefix identifying this lookup; the source and typed query are appended. */
  queryKey: readonly unknown[]
  search: (query: string, source: SuggestionSource) => Promise<Suggestion[]>
  /** False when the workflow is not known yet, which would make every query a 400. */
  enabled?: boolean
  /**
   * Replaces what the trigger shows at rest, for a value that is more than one line. The trigger
   * is still the whole block, so everything drawn here is part of the same click and the same
   * hover.
   */
  renderResting?: (value: string) => React.ReactNode
  /** Scales the magnifier to whatever it sits beside. */
  iconClassName?: string
  /**
   * How the value reads: missing is something the order cannot be created without, deviating is
   * something we found that disagrees with the document. The order confirmation page's two.
   */
  tone?: 'default' | 'missing' | 'deviating'
  /** An order already in the ERP shows what it points at and offers no way to repoint it. */
  readOnly?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const debouncedQuery = useDebounced(query, DEBOUNCE_MS)
  const trimmed = debouncedQuery.trim()
  const longEnough = trimmed.length >= MINIMUM_QUERY_LENGTH
  const ready = editing && enabled && longEnough

  // Both sources are asked at once. The local one reads tables the ERP source has already
  // filled, so it answers in milliseconds and puts something on screen while the ERP is still
  // being waited on.
  const local = useQuery({
    queryKey: [...queryKey, 'local', trimmed],
    queryFn: () => search(trimmed, 'local'),
    enabled: ready,
    staleTime: 5 * 60_000,
  })

  const remote = useQuery({
    queryKey: [...queryKey, 'erp', trimmed],
    queryFn: () => search(trimmed, 'erp'),
    enabled: ready,
    // The ERP's catalogue does not move while a salesperson works through one order, and the
    // same few queries get retyped as they correct rows.
    staleTime: 5 * 60_000,
  })

  // Local first, then whatever the ERP knew that we did not. Appending rather than reordering
  // keeps the list still under the cursor: a suggestion cannot move out from under a keypress
  // when the slower source lands.
  const list = useMemo(() => {
    const merged = local.data ?? []
    const seen = new Set(merged.map((suggestion) => suggestion.value))
    return [
      ...merged,
      ...(remote.data ?? []).filter((suggestion) => !seen.has(suggestion.value)),
    ]
  }, [local.data, remote.data])

  // The cursor belongs to a query, not to a result set: it survives the ERP's results arriving
  // and only goes back to the top when what was typed changes.
  useEffect(() => {
    setHighlighted(0)
  }, [trimmed])

  function close() {
    setEditing(false)
    setQuery('')
    setHighlighted(0)
  }

  useEffect(() => {
    if (!editing) return

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || listRef.current?.contains(target)) return
      close()
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [editing])

  function take(suggestion: Suggestion) {
    onPick(suggestion)
    close()
  }

  const anchor = useAnchorRect(anchorRef, editing)

  // Arrowing past the visible part of the list brings the highlighted suggestion into view.
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  if (readOnly) {
    return (
      <div className="-ml-1 flex w-full items-center gap-1.5 px-1 py-0.5">
        {renderResting ? (
          renderResting(value)
        ) : (
          <span
            className={cn(
              'truncate text-sm',
              !value && (tone === 'missing' ? 'text-red-600' : 'text-gray-400'),
              value && (tone === 'deviating' ? 'font-medium text-amber-700' : 'text-gray-900'),
            )}
          >
            {value || placeholder}
          </span>
        )}
      </div>
    )
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setQuery('')
          setEditing(true)
        }}
        aria-label={`Search for ${label}`}
        className="-ml-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-gray-50"
      >
        {renderResting ? (
          renderResting(value)
        ) : !value && tone === 'missing' ? (
          // The same marker as a missing match elsewhere: it flags the field, not the whole row.
          <Badge tone="danger" dot>
            {placeholder}
          </Badge>
        ) : (
          <span
            className={cn(
              'truncate text-sm',
              !value && 'text-gray-400',
              value && (tone === 'deviating' ? 'font-medium text-amber-700' : 'text-gray-900'),
            )}
          >
            {value || placeholder}
          </span>
        )}
        <Search className={cn('shrink-0 text-gray-400', iconClassName)} />
      </button>
    )
  }

  // A shrinking list can strand the cursor past the end.
  const cursor = Math.min(highlighted, Math.max(list.length - 1, 0))

  return (
    <div ref={anchorRef}>
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              close()
              return
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setHighlighted(Math.min(cursor + 1, Math.max(list.length - 1, 0)))
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              setHighlighted(Math.max(cursor - 1, 0))
              return
            }
            if (e.key === 'Enter') {
              e.preventDefault()
              const picked = list[cursor]
              if (picked) take(picked)
            }
          }}
          placeholder={`Search ${label}`}
          aria-label={`Search for ${label}`}
          aria-expanded={list.length > 0}
          autoComplete="off"
          role="combobox"
          aria-controls={`${label}-suggestions`}
          className={cn(
            'h-6 min-w-0 flex-1 rounded border border-input bg-white px-1.5 text-sm text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15',
          )}
        />
        {remote.isFetching && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" />}
        {value && (
          <button
            type="button"
            onClick={() => take({ value: '', description: '' })}
            title={`Clear ${label}`}
            aria-label={`Clear ${label}`}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          >
            <Eraser className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          onClick={close}
          aria-label={`Cancel searching for ${label}`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {longEnough && anchor && createPortal(
        <ul
          ref={listRef}
          id={`${label}-suggestions`}
          role="listbox"
          style={{ top: anchor.top, bottom: anchor.bottom, left: anchor.left, width: anchor.width, maxHeight: anchor.maxHeight }}
          className="fixed z-50 overflow-y-auto overscroll-contain rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {list.length === 0 && (local.isFetching || remote.isFetching) && (
            <li className="px-2 py-1.5 text-xs text-gray-400">Searching…</li>
          )}
          {list.length === 0 && !local.isFetching && !remote.isFetching && !remote.isError && (
            <li className="px-2 py-1.5 text-xs text-gray-400">No matches.</li>
          )}
          {list.map((suggestion, index) => (
            <li key={suggestion.value}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlighted}
                onMouseEnter={() => setHighlighted(index)}
                // mousedown rather than click: the input blurs first on a click, and the list
                // would be gone before the pick landed.
                onMouseDown={(e) => {
                  e.preventDefault()
                  take(suggestion)
                }}
                className={cn(
                  'flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs',
                  index === highlighted ? 'bg-gray-100' : 'hover:bg-gray-50',
                )}
              >
                <span className="shrink-0 font-mono text-gray-900">{suggestion.value}:</span>
                <span className="truncate text-gray-500" title={suggestion.description}>
                  {suggestion.description}
                </span>
                {index === highlighted && (
                  <Check className="ml-auto h-3 w-3 shrink-0 text-gray-400" />
                )}
              </button>
            </li>
          ))}
          {/*
            The ERP failing is worth saying even when the local tables answered, because what is
            on screen is then only what has been seen before rather than the whole catalogue.
          */}
          {remote.isError && (
            <li className="border-t border-gray-100 px-2 py-1.5 text-xs text-amber-700">
              {list.length === 0
                ? 'Could not reach the ERP.'
                : `Could not reach the ERP. Showing ${label}s seen before.`}
            </li>
          )}
        </ul>,
        document.body,
      )}
    </div>
  )
}
